<template>
  <section class="bg-gray-50 dark:bg-[#0d1117] min-h-screen">
    <!-- Header ----------------------------------------------------------
         Solid background and z-10, deliberately, matching TasksPage.vue's
         header. This was briefly `bg-white/90 backdrop-blur` at z-30, which
         hid the sidebar's collapse button on this page only: that button
         sits at z-50 inside the z-40 sidebar and pokes 14px into the main
         column, right where this header sits. `backdrop-filter` forces the
         element onto its own composited layer and browsers do not reliably
         keep it below higher z-index content in an ancestor stacking
         context, so the blurred header painted over a button that outranks
         it. Nothing here needs to sit above the sidebar, so don't
         reintroduce either the blur or the raised z-index. -->
    <div class="sticky top-0 z-10 bg-white dark:bg-[#1C2128] border-b border-gray-200 dark:border-gray-700">
      <div class="max-w-[1400px] mx-auto flex items-center justify-between gap-3 px-4 sm:px-6 h-14">
        <!-- Breadcrumb. The title repeats here only once it's scrolled out of
             reach on small screens; on desktop it's a genuine trail. -->
        <nav class="flex items-center gap-1.5 min-w-0 text-sm">
          <button
            type="button" @click="goBack"
            class="flex items-center gap-1 px-1.5 py-1 -ml-1.5 rounded-lg font-medium text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors flex-shrink-0"
          >
            <svg class="w-4 h-4 fill-current" viewBox="0 0 24 24">
              <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/>
            </svg>
            Tasks
          </button>
          <svg class="w-4 h-4 text-gray-300 dark:text-gray-600 flex-shrink-0" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7"/>
          </svg>
          <span class="flex items-center gap-2 min-w-0">
            <!-- Live hex from the local map, not the task's saved
                 `color_css_code` — otherwise the dot lags a swatch change
                 until the save round-trips. -->
            <span class="w-2 h-2 rounded-full flex-shrink-0" :style="{ backgroundColor: accentHex }"></span>
            <span class="font-medium text-gray-900 dark:text-gray-100 truncate">{{ form.title || 'Untitled task' }}</span>
          </span>
        </nav>

        <div class="flex items-center gap-1 sm:gap-2 flex-shrink-0">
          <Transition name="save-state">
            <span v-if="isDirty" key="dirty" class="hidden sm:flex items-center gap-1.5 mr-1 text-xs font-medium text-amber-600 dark:text-amber-400">
              <span class="w-1.5 h-1.5 rounded-full bg-amber-500"></span>
              Unsaved changes
            </span>
            <span v-else-if="justSaved" key="saved" class="hidden sm:flex items-center gap-1.5 mr-1 text-xs font-medium text-green-600 dark:text-green-400">
              <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7"/>
              </svg>
              Saved
            </span>
          </Transition>

          <!-- Share reads as active once a public link exists, so the
               header itself tells you the task is out in the world without
               having to open the dialog. -->
          <button
            v-if="task"
            type="button"
            @click="shareDialogOpen = true"
            class="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-lg text-sm font-medium hover:scale-105 active:scale-95 transition-all duration-150"
            :class="task.shared_publicly
              ? 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/40'
              : 'text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800'"
            :title="task.shared_publicly ? 'This task has a public link' : 'Share this task'"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.75" d="M13.19 8.688a4.5 4.5 0 0 1 1.242 7.244l-4.5 4.5a4.5 4.5 0 0 1-6.364-6.364l1.757-1.757m13.35-.622 1.757-1.757a4.5 4.5 0 0 0-6.364-6.364l-4.5 4.5a4.5 4.5 0 0 0 1.242 7.244"/>
            </svg>
            <span class="hidden sm:inline">{{ task.shared_publicly ? 'Shared' : 'Share' }}</span>
          </button>

          <button
            v-if="task"
            type="button"
            @click="store.openDeleteDialog(task, '/tasks')"
            class="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 hover:scale-105 active:scale-95 transition-all duration-150"
            aria-label="Delete task"
            title="Delete task"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.75" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
            </svg>
          </button>
          <div class="w-px h-5 bg-gray-200 dark:bg-gray-700 mx-0.5 hidden sm:block"></div>
          <button
            type="button" @click="goBack"
            class="hidden sm:block px-3 py-1.5 text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            @click="submit"
            :disabled="!canSave"
            class="inline-flex items-center gap-1.5 px-4 py-1.5 bg-blue-600 hover:bg-blue-700 hover:shadow-md active:scale-[0.97] disabled:bg-gray-200 dark:disabled:bg-gray-700 disabled:text-gray-400 dark:disabled:text-gray-500 disabled:cursor-not-allowed disabled:shadow-none disabled:active:scale-100 text-white text-sm font-semibold rounded-lg shadow-sm transition-all duration-150"
          >
            <svg v-if="saving" class="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
            </svg>
            {{ saving ? 'Saving' : 'Save' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Loading skeleton. Mirrors the real two-column structure (see
         CLAUDE.md's design system: a skeleton reproduces the loaded layout,
         it isn't a pair of grey rectangles). -->
    <div v-if="store.currentTaskLoading && !task" class="max-w-[1400px] mx-auto px-4 sm:px-6 py-6 sm:py-8 animate-pulse">
      <div class="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_320px] gap-5 lg:gap-6 items-start">
        <div class="bg-white dark:bg-[#1C2128] rounded-lg border border-gray-100 dark:border-gray-700 p-6">
          <div class="h-7 w-2/3 bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
          <div class="flex gap-2 mt-4">
            <div class="h-6 w-24 bg-gray-100 dark:bg-gray-800 rounded-full"></div>
            <div class="h-6 w-20 bg-gray-100 dark:bg-gray-800 rounded-full"></div>
            <div class="h-6 w-28 bg-gray-100 dark:bg-gray-800 rounded-full"></div>
          </div>
          <div class="mt-6 pt-5 border-t border-gray-100 dark:border-gray-700 space-y-2.5">
            <div class="h-3.5 bg-gray-100 dark:bg-gray-800 rounded w-full"></div>
            <div class="h-3.5 bg-gray-100 dark:bg-gray-800 rounded w-11/12"></div>
            <div class="h-3.5 bg-gray-100 dark:bg-gray-800 rounded w-4/6"></div>
          </div>
        </div>
        <div class="bg-white dark:bg-[#1C2128] rounded-lg border border-gray-100 dark:border-gray-700 divide-y divide-gray-100 dark:divide-gray-700">
          <div v-for="i in 5" :key="i" class="flex items-center justify-between px-4 py-3.5">
            <div class="h-3 w-20 bg-gray-200 dark:bg-gray-700 rounded"></div>
            <div class="h-5 w-24 bg-gray-100 dark:bg-gray-800 rounded-full"></div>
          </div>
        </div>
      </div>
    </div>

    <!-- Gone ------------------------------------------------------------
         A 404 is not the same failure as a network error, and offering
         "Try again" for it is a button that can never work. The common way
         to land here is following a stale link to an occurrence whose
         series was rescheduled out from under it. -->
    <div v-else-if="store.currentTaskMissing" class="flex flex-col items-center justify-center py-32 text-center px-4">
      <div class="w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center mb-4">
        <svg class="w-7 h-7 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.75" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"/>
        </svg>
      </div>
      <h3 class="text-base font-semibold text-gray-900 dark:text-white mb-1">This task no longer exists</h3>
      <p class="text-sm text-gray-500 dark:text-gray-400 mb-5 max-w-sm">
        It was deleted, or it was part of a repeating series whose schedule has since changed.
      </p>
      <button @click="router.push('/tasks')" class="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 hover:shadow-md active:scale-[0.97] transition-all duration-150">Back to tasks</button>
    </div>

    <!-- Error ----------------------------------------------------------- -->
    <div v-else-if="store.currentTaskError" class="flex flex-col items-center justify-center py-32 text-center px-4">
      <div class="w-16 h-16 rounded-full bg-red-50 dark:bg-red-900/20 flex items-center justify-center mb-4">
        <svg class="w-7 h-7 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
        </svg>
      </div>
      <h3 class="text-base font-semibold text-gray-900 dark:text-white mb-1">Something went wrong</h3>
      <p class="text-sm text-gray-500 dark:text-gray-400 mb-5">{{ store.currentTaskError }}</p>
      <button @click="load" class="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 hover:shadow-md active:scale-[0.97] transition-all duration-150">Try again</button>
    </div>

    <!-- Content ---------------------------------------------------------
         1400px, not the 1024 of `max-w-5xl`: at 5xl this page left wide
         margins empty on any normal desktop while the description editor
         was cramped. Not fully fluid like TasksPage.vue either — that one
         is a board and genuinely wants every pixel, whereas an unbounded
         prose editor on a 27" display gives ~2000px lines, which is
         unpleasant to read and to write. -->
    <div v-else class="max-w-[1400px] mx-auto px-4 sm:px-6 py-6 sm:py-8">
      <div v-if="error" class="mb-4 flex items-start gap-2.5 px-3.5 py-2.5 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/60 rounded-md text-sm text-red-700 dark:text-red-300">
        <svg class="w-4 h-4 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
        </svg>
        {{ error }}
      </div>

      <div class="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_320px] gap-5 lg:gap-6 items-start">
        <!-- Main ------------------------------------------------------- -->
        <!-- The task's own color identifies the card: a solid rule across
             the top and a wash that fades out behind the title block. Kept
             to this one card on purpose — the color says "which task is
             this", it isn't a second accent competing with blue for
             "what do I click". -->
        <div class="min-w-0 bg-white dark:bg-[#1C2128] rounded-lg border border-gray-100 dark:border-gray-700 shadow-sm hover:shadow-md transition-shadow duration-200 overflow-hidden">
          <div class="h-1" :style="{ backgroundColor: accentHex }"></div>
          <div class="p-5 sm:p-6" :style="{ backgroundImage: accentWash }">
            <input
              v-model="form.title"
              type="text"
              placeholder="What needs to be done?"
              :style="{ '--task-accent': accentHex }"
              class="task-title-input w-full text-[26px] leading-snug font-semibold tracking-tight text-gray-900 dark:text-white placeholder-gray-300 dark:placeholder-gray-600 bg-transparent border-0 rounded-lg px-2 -mx-2 py-1 transition-colors focus:outline-none"
            />

            <!-- At-a-glance state. Everything here is also editable in the
                 sidebar — this row exists so the task reads correctly
                 without having to scan a property list first. -->
            <div class="flex flex-wrap items-center gap-1.5 mt-3">
              <span
                v-if="!form.recurring"
                class="inline-flex items-center gap-1.5 pl-2 pr-2.5 py-1 rounded-full text-xs font-semibold"
                :class="STATUS_META[form.status].pill"
              >
                <span class="w-1.5 h-1.5 rounded-full" :class="STATUS_META[form.status].dot"></span>
                {{ STATUS_META[form.status].label }}
              </span>

              <span
                class="inline-flex items-center gap-1.5 pl-2 pr-2.5 py-1 rounded-full text-xs font-semibold"
                :class="PRIORITY_META[form.priority].pill"
              >
                <svg class="w-3 h-3" :class="PRIORITY_META[form.priority].text" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M3 2a1 1 0 0 1 1-1h.01a1 1 0 0 1 .99.883L5.01 2H14a1 1 0 0 1 .8 1.6L12.25 7l2.55 3.4A1 1 0 0 1 14 12H5v6a1 1 0 1 1-2 0V2z"/>
                </svg>
                {{ PRIORITY_META[form.priority].label }}
              </span>

              <span
                v-if="!form.recurring && form.dueDate"
                class="inline-flex items-center gap-1.5 pl-2 pr-2.5 py-1 rounded-full text-xs font-semibold"
                :class="isOverdueNow
                  ? 'bg-red-50 text-red-600 dark:bg-red-900/30 dark:text-red-300'
                  : 'bg-gray-100 text-gray-600 dark:bg-gray-700/60 dark:text-gray-300'"
              >
                <svg class="w-3 h-3" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5"/>
                </svg>
                {{ isOverdueNow ? 'Overdue · ' : '' }}{{ dueChipLabel }}
              </span>

              <span
                v-if="form.recurring || task?.parent_task_id"
                class="inline-flex items-center gap-1.5 pl-2 pr-2.5 py-1 rounded-full text-xs font-semibold bg-violet-50 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300"
              >
                <svg class="w-3 h-3" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99"/>
                </svg>
                {{ form.recurring ? 'Repeats' : 'In a series' }}
              </span>

              <!-- Same indigo the checklist icon and board tag use. Reads
                   from `form`, not `task`, so flipping the sidebar toggle
                   shows up here immediately rather than after a save. -->
              <span
                v-if="form.carryOver && !form.recurring && !isSeriesMode"
                class="inline-flex items-center gap-1.5 pl-2 pr-2.5 py-1 rounded-full text-xs font-semibold bg-indigo-50 text-indigo-600 dark:bg-indigo-900/25 dark:text-indigo-300"
              >
                <svg class="w-3 h-3" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M11.25 4.5l7.5 7.5-7.5 7.5m-6-15l7.5 7.5-7.5 7.5"/>
                </svg>
                Carry over
              </span>

              <span
                v-if="form.reminding && !form.recurring"
                class="inline-flex items-center gap-1.5 pl-2 pr-2.5 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-600 dark:bg-gray-700/60 dark:text-gray-300"
              >
                <svg class="w-3 h-3" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0"/>
                </svg>
                Reminder set
              </span>
            </div>
          </div>

          <!-- Description. No section label — the placeholder already says
               what this is, and a seventh uppercase micro-heading was a big
               part of what made this page read as a form dump. -->
          <!-- No border-t and no wrapper styling: quill_editor.vue now draws
               its own bordered card with its own toolbar chrome and dark
               tokens, so this is just the padding it sits in. The page used
               to restyle Quill's internals here via :deep(.ql-*) — those
               overrides are gone because they now fight the component (the
               dark .ql-stroke rules in particular were specific enough to
               grey out the active-icon state). -->
          <!-- `height` grows with the viewport instead of sitting at a fixed
               260px, which left the editor a letterbox on a normal desktop.
               Clamped so it stays usable on a short laptop screen and
               doesn't run away on a tall monitor. -->
          <div class="px-5 sm:px-6 pb-5 sm:pb-6">
            <quill-editor
              :content="descriptionSeed"
              :enable="true"
              :opts="{ height: 'clamp(320px, calc(100vh - 330px), 760px)', placeholder: 'Add details, links, or a checklist...' }"
              :upload-image="uploadDescriptionImage"
              :enable-table="true"
              @update-content="onDescriptionChange"
            />
          </div>
        </div>

        <!-- Sidebar ---------------------------------------------------- -->
        <div class="lg:sticky lg:top-[72px] space-y-4">
          <div class="rounded-lg border border-gray-100 dark:border-gray-700 shadow-sm hover:shadow-md transition-shadow duration-200 bg-white dark:bg-[#1C2128]">
            <button
              type="button"
              @click="toggleSection('details')"
              class="w-full flex items-center justify-between gap-3 px-4 py-3 text-left transition-colors duration-150 hover:bg-gray-50 dark:hover:bg-white/[0.03] rounded-t-lg"
              :class="openSection === 'details' ? 'border-b border-gray-100 dark:border-gray-700' : 'rounded-b-lg'"
            >
              <h2 class="text-sm font-semibold text-gray-900 dark:text-white">Details</h2>
              <svg
                class="w-4 h-4 flex-shrink-0 text-gray-400 dark:text-gray-500 transition-transform duration-200"
                :class="openSection === 'details' ? 'rotate-180' : ''"
                fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"
              >
                <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7"/>
              </svg>
            </button>

            <div v-if="openSection === 'details'" class="divide-y divide-gray-100 dark:divide-gray-700 py-1">
              <!-- Status (not meaningful for a series — a template has no
                   workflow state of its own, its occurrences do). -->
              <TaskPropertyRow v-if="!isSeriesMode && !form.recurring" label="Status">
                <template #icon>
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.75" d="M3 3v1.5M3 21v-6m0 0 2.77-.693a9 9 0 0 1 6.208.682l.108.054a9 9 0 0 0 6.086.71l3.114-.732a48.524 48.524 0 0 1-.005-10.499l-3.11.732a9 9 0 0 1-6.085-.711l-.108-.054a9 9 0 0 0-6.208-.682L3 4.5M3 15V4.5"/>
                  </svg>
                </template>
                <TaskSelectMenu v-model="form.status" :options="statusOptions">
                  <template #selected="{ option }">
                    <span class="inline-flex items-center gap-1.5 pl-2 pr-2.5 py-1 rounded-full text-xs font-semibold" :class="STATUS_META[option.value].pill">
                      <span class="w-1.5 h-1.5 rounded-full flex-shrink-0" :class="STATUS_META[option.value].dot"></span>
                      {{ option.label }}
                    </span>
                  </template>
                  <template #option="{ option }">
                    <span class="w-1.5 h-1.5 rounded-full flex-shrink-0" :class="STATUS_META[option.value].dot"></span>
                    {{ option.label }}
                  </template>
                </TaskSelectMenu>
              </TaskPropertyRow>

              <!-- Priority is a property of the series itself, so it shows in
                   every mode. -->
              <TaskPropertyRow label="Priority">
                <template #icon>
                  <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M3 2a1 1 0 0 1 1-1h.01a1 1 0 0 1 .99.883L5.01 2H14a1 1 0 0 1 .8 1.6L12.25 7l2.55 3.4A1 1 0 0 1 14 12H5v6a1 1 0 1 1-2 0V2z"/>
                  </svg>
                </template>
                <TaskSelectMenu v-model="form.priority" :options="priorityOptions">
                  <template #selected="{ option }">
                    <span class="inline-flex items-center gap-1.5 pl-2 pr-2.5 py-1 rounded-full text-xs font-semibold" :class="PRIORITY_META[option.value].pill">
                      <svg class="w-3 h-3 flex-shrink-0" :class="PRIORITY_META[option.value].text" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M3 2a1 1 0 0 1 1-1h.01a1 1 0 0 1 .99.883L5.01 2H14a1 1 0 0 1 .8 1.6L12.25 7l2.55 3.4A1 1 0 0 1 14 12H5v6a1 1 0 1 1-2 0V2z"/>
                      </svg>
                      {{ option.label }}
                    </span>
                  </template>
                  <template #option="{ option }">
                    <svg class="w-3.5 h-3.5 flex-shrink-0" :class="PRIORITY_META[option.value].text" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M3 2a1 1 0 0 1 1-1h.01a1 1 0 0 1 .99.883L5.01 2H14a1 1 0 0 1 .8 1.6L12.25 7l2.55 3.4A1 1 0 0 1 14 12H5v6a1 1 0 1 1-2 0V2z"/>
                    </svg>
                    {{ option.label }}
                  </template>
                </TaskSelectMenu>
              </TaskPropertyRow>

              <!-- Due date + reminder: one-time tasks and occurrences only.
                   A series' dates come from its recurrence rule. -->
              <template v-if="!isSeriesMode && !form.recurring">
                <!-- Inline, and the same TaskDueDateDropdown the checklist
                     rows use: "Tomorrow" is one click instead of finding the
                     right cell in a calendar, and a due date now behaves
                     identically wherever it is edited. -->
                <TaskPropertyRow label="Due date">
                  <template #icon>
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.75" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5"/>
                    </svg>
                  </template>
                  <TaskDueDateDropdown
                    :due-at="form.dueDate ? `${form.dueDate}T23:59:00` : null"
                    :overdue="isOverdueNow"
                    @update="onDueDatePicked"
                  />
                </TaskPropertyRow>

                <!-- Carry over. Sits directly under Due date because it only
                     makes sense in relation to one: it fills in the stretch
                     between now and that date, which is otherwise the window
                     where a task is invisible in Today. Past the due date a
                     task already carries forward on its own, so this changes
                     nothing then. -->
                <TaskPropertyRow label="Carry over" stacked>
                  <template #icon>
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.75" d="M11.25 4.5l7.5 7.5-7.5 7.5m-6-15l7.5 7.5-7.5 7.5"/>
                    </svg>
                  </template>
                  <template #trailing>
                    <Toggle v-model="form.carryOver" />
                  </template>
                  <p v-if="form.carryOver" class="text-xs text-gray-400 dark:text-gray-500 leading-relaxed">
                    Shows in Today every day until you finish it. Pause the task to stop it without cancelling.
                  </p>
                </TaskPropertyRow>

                <TaskPropertyRow label="Reminder" stacked>
                  <template #icon>
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.75" d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0"/>
                    </svg>
                  </template>
                  <template #trailing>
                    <Toggle v-model="form.reminding" />
                  </template>
                  <div v-if="form.reminding">
                    <div class="grid grid-cols-2 gap-2">
                      <DatePicker v-model="form.remindDate" placeholder="No date" input-class="px-3 py-2 text-sm rounded-lg" />
                      <TaskTimeField v-model="form.remindTime" />
                    </div>
                    <p class="mt-2 text-xs text-gray-400 dark:text-gray-500">
                      Shown in your timezone{{ userTzLabel ? ` (${userTzLabel})` : '' }}.
                    </p>
                  </div>
                </TaskPropertyRow>
              </template>

              <!-- Color -->
              <!-- Inline like every other row. An always open grid of 11
                   swatches gave a rarely changed property more of the card
                   than Status and Due date combined, and left the eye no
                   obvious current value to read. -->
              <TaskPropertyRow label="Color">
                <template #icon>
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.75" d="M4.098 19.902a3.75 3.75 0 0 0 5.304 0l6.401-6.402M6.75 21A3.75 3.75 0 0 1 3 17.25V4.125C3 3.504 3.504 3 4.125 3h5.25c.621 0 1.125.504 1.125 1.125v4.072M6.75 21a3.75 3.75 0 0 0 3.75-3.75V8.197M6.75 21h13.125c.621 0 1.125-.504 1.125-1.125v-5.25c0-.621-.504-1.125-1.125-1.125h-4.072M10.5 8.197l2.88-2.88c.438-.439 1.15-.439 1.59 0l3.712 3.713c.44.44.44 1.152 0 1.59l-2.879 2.88M6.75 17.25h.008v.008H6.75v-.008Z"/>
                  </svg>
                </template>
                <div class="relative">
                  <button
                    type="button"
                    @click="colorMenuOpen = !colorMenuOpen"
                    class="group inline-flex items-center gap-2 rounded-lg px-1.5 py-1 -mr-1.5 transition-all duration-150 hover:bg-gray-100 dark:hover:bg-gray-800 active:scale-[0.97]"
                    :class="colorMenuOpen ? 'bg-gray-100 dark:bg-gray-800' : ''"
                  >
                    <span class="w-4 h-4 rounded-full flex-shrink-0" :style="{ backgroundColor: accentHex }"></span>
                    <span class="text-[13px] font-medium text-gray-700 dark:text-gray-200">{{ accentLabel }}</span>
                    <svg class="w-3.5 h-3.5 flex-shrink-0 text-gray-300 dark:text-gray-600 transition-colors group-hover:text-gray-500 dark:group-hover:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
                    </svg>
                  </button>

                  <div v-if="colorMenuOpen" @click="colorMenuOpen = false" class="fixed inset-0 z-20"></div>
                  <!-- Explicit width, not shrink to fit. An absolutely
                       positioned box sizes against its nearest positioned
                       ancestor, which here is the little `relative` wrapper
                       around the trigger, so the panel collapsed to roughly
                       the trigger's width. Six fixed 24px swatches cannot
                       shrink into that, so they overflowed their grid tracks
                       and sat on top of each other. 6x24 + 5x8 gap + 2x10
                       padding = 204px, so w-52 (208px) fits with slack. -->
                  <div v-if="colorMenuOpen" class="absolute right-0 top-full mt-1.5 w-52 p-2.5 bg-white dark:bg-[#2A3037] border border-gray-100 dark:border-gray-700 rounded-xl shadow-lg ring-1 ring-black/5 z-30">
                    <!-- The selected swatch rings in its own color rather
                         than a neutral gray: the ring identifies the choice,
                         so it should read as part of it. -->
                    <div class="grid grid-cols-6 gap-2 justify-items-center">
                      <button
                        v-for="color in TASK_COLORS" :key="color.value"
                        type="button"
                        @click="form.color = color.value; colorMenuOpen = false"
                        :title="color.label"
                        :aria-label="color.label"
                        :aria-pressed="form.color === color.value"
                        class="w-6 h-6 rounded-full flex items-center justify-center transition-all duration-150 flex-shrink-0 active:scale-95"
                        :class="[color.value, form.color === color.value ? '' : 'hover:scale-110 hover:shadow-md']"
                        :style="swatchOutline(color.value)"
                      >
                        <svg v-if="form.color === color.value" class="w-3 h-3 fill-white" viewBox="0 0 24 24">
                          <path d="m10 15.586-3.293-3.293-1.414 1.414L10 18.414l9.707-9.707-1.414-1.414z"/>
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              </TaskPropertyRow>
            </div>
          </div>

          <!-- Repeat ------------------------------------------------------
               Its own card rather than a row in Details: the recurrence
               editor is a small form in its own right, and burying it in a
               property list was why the sidebar felt bottomless. -->
          <div
            v-if="isSeriesMode || !task?.parent_task_id"
            class="rounded-lg border border-gray-100 dark:border-gray-700 shadow-sm hover:shadow-md transition-shadow duration-200 bg-white dark:bg-[#1C2128]"
          >
            <div
              class="flex items-center justify-between gap-3 px-4 py-3 rounded-t-lg transition-colors duration-150"
              :class="repeatExpanded ? 'border-b border-gray-100 dark:border-gray-700' : 'rounded-b-lg'"
            >
              <!-- The heading is the accordion trigger; the on/off Toggle is
                   a separate control beside it, so switching Repeat on does
                   not also collapse whichever section you were reading. -->
              <button
                type="button"
                @click="toggleSection('repeat')"
                class="flex-1 min-w-0 flex items-center gap-2 text-left -my-1 py-1 rounded-md transition-colors duration-150 hover:opacity-80"
                :disabled="!showRecurrenceEditor"
              >
                <span class="min-w-0">
                  <span class="block text-sm font-semibold text-gray-900 dark:text-white">Repeat</span>
                  <span v-if="!isSeriesMode" class="block text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                    {{ form.recurring ? 'Turns this into a recurring series' : 'Off' }}
                  </span>
                </span>
                <svg
                  v-if="showRecurrenceEditor"
                  class="w-4 h-4 flex-shrink-0 text-gray-400 dark:text-gray-500 transition-transform duration-200"
                  :class="repeatExpanded ? 'rotate-180' : ''"
                  fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"
                >
                  <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7"/>
                </svg>
              </button>
              <Toggle v-if="!isSeriesMode" v-model="form.recurring" />
            </div>
            <div v-if="repeatExpanded" class="px-4 py-4">
              <TaskRecurrenceEditor v-model="form.recurrenceRule" />
            </div>
          </div>

          <!-- Series (occurrences only) -->
          <div
            v-if="task?.parent_task_id"
            class="rounded-lg border border-gray-100 dark:border-gray-700 shadow-sm hover:shadow-md transition-shadow duration-200 bg-white dark:bg-[#1C2128]"
          >
            <button
              type="button"
              @click="toggleSection('series')"
              class="w-full flex items-center justify-between gap-3 px-4 py-3 text-left rounded-t-lg transition-colors duration-150 hover:bg-gray-50 dark:hover:bg-white/[0.03]"
              :class="seriesEditorOpen ? 'border-b border-gray-100 dark:border-gray-700' : 'rounded-b-lg'"
            >
              <span class="min-w-0">
                <span class="block text-sm font-semibold text-gray-900 dark:text-white">Series</span>
                <span class="block text-xs text-gray-400 dark:text-gray-500 mt-0.5">One occurrence of a repeating task</span>
              </span>
              <span class="flex items-center gap-1.5 flex-shrink-0 text-xs font-semibold text-blue-600 dark:text-blue-400">
                {{ seriesEditorOpen ? 'Hide' : 'Edit schedule' }}
                <svg
                  class="w-4 h-4 transition-transform duration-200"
                  :class="seriesEditorOpen ? 'rotate-180' : ''"
                  fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"
                >
                  <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7"/>
                </svg>
              </span>
            </button>
            <div v-if="seriesEditorOpen" class="px-4 py-4 space-y-3">
              <TaskRecurrenceEditor v-if="seriesRule" v-model="seriesRule" />
              <p v-else class="text-xs text-gray-400 dark:text-gray-500">Loading schedule...</p>
              <button
                type="button"
                @click="saveSeriesSchedule"
                :disabled="savingSeries || !seriesRule"
                class="w-full h-9 text-xs font-semibold text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/40 active:scale-[0.98] rounded-lg transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100"
              >{{ savingSeries ? 'Updating...' : 'Update schedule' }}</button>
              <p class="text-xs text-gray-400 dark:text-gray-500 leading-relaxed">
                Changing the schedule rebuilds upcoming occurrences. Ones you have already completed stay as they are.
              </p>
            </div>
          </div>

          <!-- Public link. Only present while the task is actually shared,
               and deliberately not part of the accordion: once a task is
               public, how it is doing out there is a standing fact worth
               seeing without opening anything. -->
          <div
            v-if="task?.shared_publicly"
            class="rounded-lg border border-blue-100 dark:border-blue-900/40 shadow-sm bg-blue-50/50 dark:bg-blue-900/10"
          >
            <div class="flex items-center justify-between gap-3 px-4 py-3 border-b border-blue-100 dark:border-blue-900/40">
              <h2 class="flex items-center gap-2 text-sm font-semibold text-blue-900 dark:text-blue-200">
                <svg class="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M13.19 8.688a4.5 4.5 0 0 1 1.242 7.244l-4.5 4.5a4.5 4.5 0 0 1-6.364-6.364l1.757-1.757m13.35-.622 1.757-1.757a4.5 4.5 0 0 0-6.364-6.364l-4.5 4.5a4.5 4.5 0 0 0 1.242 7.244"/>
                </svg>
                Public link
              </h2>
              <button
                type="button"
                @click="shareDialogOpen = true"
                class="px-2.5 py-1 text-xs font-semibold text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/30 active:scale-95 rounded-lg transition-all duration-150 flex-shrink-0"
              >Manage</button>
            </div>

            <div class="px-4 py-3 space-y-3">
              <div class="flex items-baseline gap-2">
                <span class="text-2xl font-bold text-blue-900 dark:text-blue-100 tabular-nums">{{ task.unique_public_views ?? 0 }}</span>
                <span class="text-[13px] text-blue-700/80 dark:text-blue-300/80">
                  {{ (task.unique_public_views ?? 0) === 1 ? 'person has viewed this' : 'people have viewed this' }}
                </span>
              </div>

              <!-- Counted per person rather than per page load, so someone
                   reloading does not inflate it. Say so, or the number looks
                   wrong to anyone who has refreshed their own link. -->
              <p class="text-xs text-blue-700/70 dark:text-blue-300/70 leading-relaxed">
                Unique visitors, not page loads. Your own visits are not counted.
              </p>

              <div class="flex items-center gap-2 pt-1">
                <a
                  :href="task.public_url" target="_blank" rel="noopener noreferrer"
                  class="flex-1 text-center px-3 py-1.5 text-xs font-semibold text-blue-700 dark:text-blue-300 bg-white dark:bg-[#1C2128] border border-blue-200 dark:border-blue-900/50 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20 active:scale-[0.98] transition-all duration-150"
                >Open page</a>
                <button
                  type="button"
                  @click="copyPublicUrl"
                  class="flex-1 px-3 py-1.5 text-xs font-semibold rounded-lg transition-all duration-150 active:scale-[0.98]"
                  :class="publicUrlCopied
                    ? 'bg-green-600 text-white'
                    : 'bg-blue-600 hover:bg-blue-700 text-white'"
                >{{ publicUrlCopied ? 'Copied' : 'Copy link' }}</button>
              </div>
            </div>
          </div>

          <!-- Quiet provenance footer. `added_by_display_id` already comes
               back on the task (TaskBlueprint) and had nowhere to surface. -->
          <dl v-if="task" class="px-4 space-y-1.5 text-xs text-gray-400 dark:text-gray-500">
            <div v-if="task.added_by_display_id" class="flex items-center justify-between gap-3 px-2 py-1 -mx-2 rounded-md hover:bg-gray-100/70 dark:hover:bg-white/[0.03] transition-colors duration-150">
              <dt>Added by</dt>
              <dd class="font-medium text-gray-500 dark:text-gray-400">{{ task.added_by_display_id }}</dd>
            </div>
            <div class="flex items-center justify-between gap-3 px-2 py-1 -mx-2 rounded-md hover:bg-gray-100/70 dark:hover:bg-white/[0.03] transition-colors duration-150">
              <dt>Created</dt>
              <dd class="font-medium text-gray-500 dark:text-gray-400">{{ formatDueDateLabel(task.created_at) }}</dd>
            </div>
            <div class="flex items-center justify-between gap-3 px-2 py-1 -mx-2 rounded-md hover:bg-gray-100/70 dark:hover:bg-white/[0.03] transition-colors duration-150">
              <dt>Last updated</dt>
              <dd class="font-medium text-gray-500 dark:text-gray-400">{{ formatDueDateLabel(task.updated_at) }}</dd>
            </div>
          </dl>
        </div>
      </div>
    </div>
  </section>

  <TaskDeleteDialog />
  <TaskShareDialog :open="shareDialogOpen" :task="task" @close="shareDialogOpen = false" />
  <TaskEditScopeDialog
    :open="scopeDialogOpen"
    :saving="saving"
    @confirm="onScopeConfirm"
    @cancel="onScopeCancel"
  />
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useGlobalStore } from 'stores/global.js'
import { useTasksStore } from 'stores/tasks.js'
import { useRequest } from 'composables/useRequest.js'
import { useTaskDates } from 'composables/useTaskDates.js'
import { useFormatting } from 'composables/useFormatting.js'
import { useImageCompression } from 'composables/useImageCompression.js'
import TaskRecurrenceEditor from './TaskRecurrenceEditor.vue'
import Toggle from './Toggle.vue'
import TaskTimeField from './TaskTimeField.vue'
import TaskDueDateDropdown from './TaskDueDateDropdown.vue'
import TaskDeleteDialog from './TaskDeleteDialog.vue'
import TaskEditScopeDialog from './TaskEditScopeDialog.vue'
import TaskShareDialog from './TaskShareDialog.vue'
import TaskPropertyRow from './TaskPropertyRow.vue'
import TaskSelectMenu from './TaskSelectMenu.vue'
import DatePicker from 'controllers/vue_components/shared/DatePicker.vue'
import { TASK_COLORS, TASK_COLOR_HEX } from './taskColors.js'
import { PRIORITY_META, PRIORITY_ORDER } from './taskPriorities.js'

const route = useRoute()
const router = useRouter()
const globalStore = useGlobalStore()
const store = useTasksStore()
const { axiosRequest } = useRequest()
const {
  userTz, dateStrInUserTz, timeStrInUserTz, todayStrInUserTz, formatDueDateLabel, addDaysToDateStr,
} = useTaskDates()
const { dateFormatMask, formatDateInMask } = useFormatting()
const { compressImage } = useImageCompression()

// This page is reachable directly (e.g. the reminder email's "View Task"
// link), not only via /tasks — same staleness concern as TasksPage.vue's
// own refreshUser() call: the cached date_format/timezone in the store can
// be stale, and DatePicker's due-date/reminder-date fields below would
// silently render/parse using the wrong format otherwise.
onMounted(() => { globalStore.refreshUser() })

const saving = ref(false)
const error = ref(null)

// Transient "Saved" confirmation in the header. Needed precisely because
// saving no longer navigates away: without it a successful save on a page
// that doesn't move produces no visible feedback at all.
const justSaved = ref(false)
let justSavedTimer = null
onBeforeUnmount(() => clearTimeout(justSavedTimer))

const task = computed(() => store.currentTask)
const isSeriesMode = computed(() => task.value?.task_type === 'recurring_series')
const userTzLabel = computed(() => { try { return userTz() } catch { return '' } })

const STATUS_META = {
  pending:   { label: 'Pending',   dot: 'bg-gray-400',   pill: 'bg-gray-100 text-gray-700 dark:bg-gray-700/60 dark:text-gray-300' },
  started:   { label: 'Started',   dot: 'bg-blue-500',   pill: 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' },
  paused:    { label: 'Paused',    dot: 'bg-amber-500',  pill: 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' },
  completed: { label: 'Completed', dot: 'bg-green-500',  pill: 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300' },
  canceled:  { label: 'Canceled',  dot: 'bg-red-400',    pill: 'bg-red-50 text-red-600 dark:bg-red-900/30 dark:text-red-300' },
}
// Mirrors Task::ALLOWED_TRANSITIONS — pending/started/paused/completed are
// freely interchangeable (matches the Kanban board's own drag-and-drop
// rules), canceled is a one-way terminal exit.
const ALLOWED_TRANSITIONS = {
  pending: ['pending', 'started', 'paused', 'completed', 'canceled'],
  started: ['started', 'pending', 'paused', 'completed', 'canceled'],
  paused: ['paused', 'pending', 'started', 'completed', 'canceled'],
  completed: ['completed', 'pending', 'started', 'paused', 'canceled'],
  canceled: ['canceled'],
}

const statusOptions = computed(() => {
  const current = task.value?.status || 'pending'
  const allowed = ALLOWED_TRANSITIONS[current] || Object.keys(STATUS_META)
  return Object.keys(STATUS_META).map((value) => ({
    value, label: STATUS_META[value].label, disabled: !allowed.includes(value),
  }))
})

const priorityOptions = computed(() =>
  PRIORITY_ORDER.map((value) => ({ value, label: PRIORITY_META[value].label }))
)

// ── Task color as a page accent ──────────────────────────────────────────
// Driven by `form.color`, not the task's saved `color_css_code`, so picking
// a swatch retints the page immediately instead of after a save.
//
// Inline styles from a JS hex map, per CLAUDE.md's "dynamic colors from
// backend" rule — the 11 palette names are hand-authored CSS classes, so a
// dynamic Tailwind class string (`ring-${color}`) would never make it into
// the build.
const accentHex = computed(() => TASK_COLOR_HEX[form.value.color] || TASK_COLOR_HEX.peacock)
const accentLabel = computed(
  () => TASK_COLORS.find((c) => c.value === form.value.color)?.label || ''
)

// One alpha for both themes rather than branching on the current theme:
// `document.documentElement.classList` is read once and wouldn't react to a
// theme switch anyway. 12% of a saturated hue is a pale tint over white and
// a soft haze over #1C2128 — legible in both without washing either out.
// `outline` + `outline-offset` rather than Tailwind's `ring-*`: a ring needs
// an offset color matched to whatever is behind it (so it would have to know
// the current theme's card background), while an outline offset is simply
// transparent. It follows border-radius in every current browser, so the
// selected swatch reads as a concentric halo in its own color.
function swatchOutline(colorValue) {
  if (form.value.color !== colorValue) return null
  return { outline: `2px solid ${TASK_COLOR_HEX[colorValue]}`, outlineOffset: '2px' }
}

const accentWash = computed(() => {
  const hex = accentHex.value.replace('#', '')
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(hex.slice(i, i + 2), 16))
  return `linear-gradient(to bottom, rgba(${r}, ${g}, ${b}, 0.12), rgba(${r}, ${g}, ${b}, 0))`
})

// Repeat has something to show at all only for an actual series, or once a
// plain task's Repeat toggle is switched on.
const showRecurrenceEditor = computed(() => isSeriesMode.value || form.value.recurring)

// Chips in the title block read from `form`, not `task`, so they track edits
// live — flipping Priority in the sidebar updates the chip immediately
// rather than after a save round-trip.
//
// `form.dueDate` is already a plain 'YYYY-MM-DD' calendar date in the
// account's timezone, so it must NOT be round-tripped through
// `formatDueDateLabel` (which takes a UTC instant and converts it back).
// Feeding it a naive string would have it parsed in the *browser's* zone
// and then re-read in the account's, which shifts the date by a day for
// anyone whose two zones disagree — the exact bug useTaskDates.js's header
// comment warns about. Pinning the helper to midday UTC keeps the
// formatted output identical to the input date.
const dueChipLabel = computed(() => {
  const dateStr = form.value.dueDate
  if (!dateStr) return ''
  if (dateStr === todayStrInUserTz()) return 'Today'
  if (dateStr === addDaysToDateStr(todayStrInUserTz(), 1)) return 'Tomorrow'
  return formatDateInMask(`${dateStr}T12:00:00Z`, dateFormatMask(globalStore.user?.date_format), 'UTC')
})

const isOverdueNow = computed(() => {
  if (!form.value.dueDate || ['completed', 'canceled'].includes(form.value.status)) return false
  return form.value.dueDate < todayStrInUserTz()
})

// DatePicker.vue's v-model is a plain 'YYYY-MM-DD' string (or ''), same
// shape used here for dueDate/remindDate. Must go through the same
// userTz()-aware conversion the checklist/board use (useTaskDates.js) —
// `new Date(iso).getDate()` etc. reads the *browser's* system timezone,
// which silently disagrees with the account's configured `user.timezone`
// whenever they're not the same zone, showing a different due date on this
// page than on /tasks for the exact same task (a real bug this replaced).
function splitDateTime(iso) {
  if (!iso) return { date: '', time: '' }
  return { date: dateStrInUserTz(iso), time: timeStrInUserTz(iso) }
}

// TaskDueDateDropdown speaks full ISO instants (it is shared with the
// checklist rows); this page's form holds a plain 'YYYY-MM-DD'. Split on the
// literal T rather than parsing to a Date, which would reinterpret the
// instant in the browser's zone and can shift the day.
function onDueDatePicked(isoOrNull) {
  form.value.dueDate = isoOrNull ? isoOrNull.split('T')[0] : ''
}

function todayDateStr() {
  return todayStrInUserTz()
}

// Due dates are date-only in the UI — the underlying due_at is always saved
// at end-of-day (23:59), same default the backend generator job
// (GenerateRecurringOccurrencesJob#due_at_for) already uses for a recurring
// occurrence with no explicit due_time. New tasks default to due today.
function blankForm() {
  return {
    title: '', description: '', color: 'peacock', priority: 'medium', recurring: false, recurrenceRule: null,
    dueDate: todayDateStr(), status: 'pending', reminding: false, remindDate: '', remindTime: '',
    carryOver: false,
  }
}

function buildFormFromTask(t) {
  const due = splitDateTime(t.due_at)
  const remind = splitDateTime(t.remind_at)
  return {
    title: t.title || '', description: t.description || '', color: t.color || 'peacock',
    priority: t.priority || 'medium',
    recurring: t.task_type === 'recurring_series', recurrenceRule: t.recurrence_rule || null,
    dueDate: due.date, status: t.status || 'pending',
    reminding: !!t.remind_at, remindDate: remind.date, remindTime: remind.time,
    carryOver: !!t.carry_over,
  }
}

const form = ref(blankForm())

// Snapshot of the form as it was when the task loaded, so the header can
// tell the user whether they have anything worth saving. Previously Save
// was always enabled and always fired a PATCH, even on a page you just
// opened and didn't touch.
const pristine = ref(JSON.stringify(blankForm()))
const isDirty = computed(() => JSON.stringify(form.value) !== pristine.value)
const canSave = computed(() => !saving.value && !!task.value && !!form.value.title.trim() && isDirty.value)

// Leaving with unsaved edits should say so rather than silently discarding
// them — the old Cancel button dropped everything without a word.
function goBack() {
  if (isDirty.value && !window.confirm('You have unsaved changes. Leave without saving?')) return
  router.push('/tasks')
}
// See TaskDetailDrawer's original note (this page replaced it): kept
// deliberately out of sync with form.description while typing, or
// quill-editor's `content`-changed watcher resets the cursor on every
// keystroke. Only reset when a task is loaded; @update-content writes
// straight into form.description, which is what actually gets submitted.
const descriptionSeed = ref('')
const seriesRule = ref(null)

// ── Sidebar accordion ────────────────────────────────────────────────────
// One panel open at a time. Details, Repeat, and Series are each a small
// form, and with all three expanded the sidebar ran far past the fold, so
// reaching Series meant scrolling through everything above it. Details is
// open by default because it is what the page is mostly for.
const openSection = ref('details')

function toggleSection(key) {
  openSection.value = openSection.value === key ? null : key
  // The series rule is fetched lazily, on first expand, the same way the old
  // standalone "Edit schedule" button did it.
  if (openSection.value === 'series' && !seriesRule.value) fetchSeriesRule()
}

const repeatExpanded = computed(() => showRecurrenceEditor.value && openSection.value === 'repeat')
const seriesEditorOpen = computed(() => openSection.value === 'series')

// Flipping Repeat on should reveal the schedule it just enabled rather than
// leaving an empty switched on section behind whatever panel is open.
watch(() => form.value.recurring, (on) => {
  if (on && !isSeriesMode.value) openSection.value = 'repeat'
})
const savingSeries = ref(false)

function onDescriptionChange(html) {
  form.value.description = html
}

// Handed to <quill-editor> as its `uploadImage` prop. Contract: takes the
// raw File, resolves to a URL string on success or null on failure, and
// never rejects — the editor uses null to mean "leave the document alone".
//
// Uploads against the task that's already open, so an image can only ever
// attach to a task this account owns (the endpoint scopes by @api_user).
// `useRequest` detects FormData and lets the browser set the multipart
// boundary itself, so the Content-Type must not be set here.
async function uploadDescriptionImage(file) {
  if (!task.value) return null

  // Downscale + re-encode before the bytes ever leave the browser, so a
  // 20MB phone photo is stored as a few hundred KB. The server's own size
  // cap is a backstop for non-browser callers, not the thing users are
  // expected to hit.
  const compressed = await compressImage(file)

  const body = new FormData()
  body.append('file', compressed)

  const res = await axiosRequest('POST', `/api/v1/tasks/${task.value.id}/attach_image`, body, false)
  if (!res.js_error && res.response.success) return res.response.data.url

  // render_failure's payload key is `message` (see concerns/notifications.rb),
  // not `error` — reading `error` here would silently always fall through to
  // the generic string and swallow the real reason.
  error.value = res.response?.message || 'Could not upload that image.'
  return null
}

function resetUiState() {
  error.value = null
  saving.value = false
  openSection.value = 'details'
  seriesRule.value = null
}

async function load() {
  resetUiState()
  store.clearCurrentTask()
  await store.fetchTask(route.params.id)
}

watch(task, (t) => {
  if (!t) return
  form.value = buildFormFromTask(t)
  descriptionSeed.value = form.value.description
  pristine.value = JSON.stringify(form.value)
})

watch(() => route.params.id, load)

onMounted(load)

async function fetchSeriesRule() {
  const res = await axiosRequest('GET', `/api/v1/tasks/${task.value.parent_task_id}`, {}, false)
  if (!res.js_error && res.response.success) {
    seriesRule.value = res.response.data.recurrence_rule
  }
}

async function saveSeriesSchedule() {
  savingSeries.value = true
  try {
    const updated = await store.updateTask(task.value.parent_task_id, { task_type: 'recurring_series', recurrence_rule: seriesRule.value })
    if (updated) openSection.value = 'details'
  } finally {
    savingSeries.value = false
  }
}

function combineDateTime(date, time) {
  if (!date) return null
  return `${date}T${time || '00:00'}:00`
}

// Due dates have no time picker in the UI — always saved end-of-day.
function endOfDay(date) {
  return date ? `${date}T23:59:00` : null
}

// Shared fields are the ones the backend will propagate across a series
// (SHARED_ATTRIBUTES in Api::V1::TasksController). The scope prompt is only
// worth showing when one of them actually changed: if the user just ticked
// this occurrence off or moved its due date, there is nothing that *could*
// apply to the rest of the series, and asking would be a dialog with only
// one correct answer.
const SHARED_FIELDS = ['title', 'description', 'color', 'priority']

function sharedFieldsChanged() {
  if (!task.value) return false
  const before = JSON.parse(pristine.value)
  return SHARED_FIELDS.some((field) => before[field] !== form.value[field])
}

const colorMenuOpen = ref(false)
const shareDialogOpen = ref(false)

const publicUrlCopied = ref(false)
let publicUrlCopiedTimer = null
onBeforeUnmount(() => clearTimeout(publicUrlCopiedTimer))

async function copyPublicUrl() {
  const url = task.value?.public_url
  if (!url) return
  try {
    await navigator.clipboard.writeText(url)
  } catch {
    // The clipboard API needs a secure context and can be blocked outright.
    // Failing silently is worse than doing nothing visible, so bail without
    // claiming success.
    return
  }
  publicUrlCopied.value = true
  clearTimeout(publicUrlCopiedTimer)
  publicUrlCopiedTimer = setTimeout(() => { publicUrlCopied.value = false }, 2000)
}
const scopeDialogOpen = ref(false)

function onScopeCancel() {
  scopeDialogOpen.value = false
}

function onScopeConfirm(scope) {
  scopeDialogOpen.value = false
  persist(scope)
}

async function submit() {
  error.value = null
  if (!form.value.title.trim() || !task.value) return

  // An occurrence of a repeating task: ask where the edit should land before
  // writing anything, the same way deleting one does.
  if (task.value.parent_task_id && sharedFieldsChanged()) {
    scopeDialogOpen.value = true
    return
  }

  await persist(null)
}

async function persist(scope) {
  if (!task.value) return

  // form.recurring can only be true here for a plain task being turned into
  // a series for the first time — the toggle is hidden for occurrences
  // (task?.parent_task_id) and isSeriesMode already takes the branch above.
  const convertingToRecurring = !isSeriesMode.value && form.value.recurring

  const payload = {
    title: form.value.title.trim(),
    description: form.value.description || '',
    color: form.value.color,
    priority: form.value.priority,
  }

  if (isSeriesMode.value || convertingToRecurring) {
    if (!form.value.recurrenceRule?.frequency) {
      error.value = 'Please configure the recurrence.'
      return
    }
    payload.task_type = 'recurring_series'
    payload.recurrence_rule = form.value.recurrenceRule
  } else {
    payload.due_at = endOfDay(form.value.dueDate)
    payload.remind_at = form.value.reminding ? combineDateTime(form.value.remindDate, form.value.remindTime) : null
    payload.carry_over = form.value.carryOver
    payload.status = form.value.status
  }

  saving.value = true
  try {
    const updated = await store.updateTask(task.value.id, payload, scope)
    if (!updated) {
      error.value = 'Could not save task.'
      return
    }
    // Stay on the page. Saving used to navigate back to /tasks, which threw
    // the user out of a task they were probably still working on and made
    // any follow-up edit a fresh round trip. `store.updateTask` patches
    // `currentTask`, so the `task` watcher rebuilds the form and resets
    // `pristine` for us, which is what drops `isDirty` back to false.
    justSaved.value = true
    clearTimeout(justSavedTimer)
    justSavedTimer = setTimeout(() => { justSaved.value = false }, 2400)
  } finally {
    saving.value = false
  }
}
</script>

<style scoped>
/* Subtle hover fill so the title reads as interactive before it's focused.
   The focus state itself is the app's standard ring (focus:ring-blue-500 on
   the element), not custom CSS: @tailwindcss/forms (see
   app/assets/tailwind/application.css) reskins every bare input/textarea
   with its own `:focus` box-shadow + border-color at higher specificity
   than a plain class selector, so countering it needs an actual `focus:`
   Tailwind variant, not hand-written CSS like `.foo { box-shadow: none }`
   with no `:focus` in the selector. That was tried here and silently did
   nothing; don't reintroduce it.

   The fill is translucent rather than an opaque gray: the title sits on the
   task's color wash (see accentWash), and an opaque background would punch
   a flat gray rectangle through it. A low-alpha overlay darkens whatever is
   actually behind it instead.

   Quill's own chrome is deliberately NOT styled from here any more —
   quill_editor.vue owns its toolbar, container, and dark tokens. The page
   used to carry ~57 lines of :deep(.ql-*) overrides that now duplicate or
   actively fight it. */
.task-title-input:hover { background: rgb(0 0 0 / 0.035); }
:global(.dark) .task-title-input:hover { background: rgb(255 255 255 / 0.05); }

/* Focus ring in the task's own color rather than the generic blue. The
   title sits on the color wash, and a blue ring around it fought the accent
   for no reason.

   Written by hand rather than as a `focus:ring-*` utility because the color
   is dynamic: a `ring-${color}` class string would never survive the
   Tailwind build (see CLAUDE.md on dynamic colors from the backend), so it
   comes in as a custom property set inline on the element.

   This is the exception the note above allows: @tailwindcss/forms restyles
   inputs at `[type='text']:focus`, and a hand written rule only beats it
   when the selector itself carries `:focus`. Scoping adds a data attribute
   on top, which puts this above the plugin on specificity as well as order.
   Do not rewrite it as a plain `.task-title-input { ... }` block. */
.task-title-input:focus {
  box-shadow: 0 0 0 2px var(--task-accent, #4386f5);
  border-color: transparent;
}

/* Header save-state swap (Unsaved changes <-> Saved). `absolute` on the
   leaving element so the two never briefly sit side by side and shove the
   buttons around mid-transition. */
.save-state-enter-active,
.save-state-leave-active {
  transition: opacity 0.18s ease, transform 0.18s ease;
}
.save-state-enter-from { opacity: 0; transform: translateY(-3px); }
.save-state-leave-to { opacity: 0; transform: translateY(3px); }
.save-state-leave-active { position: absolute; }

</style>
